import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { AdminService } from '../../services/admin.service';
import type {
  SurveyVersionDto,
  SurveyStateDto,
  SurveyQuestionDto,
  SurveyAnswerDto,
  PromptFragmentDto,
  FragmentTriggerRowDto,
  CallToActionThresholdRowDto
} from '../../models/admin-dto';

@Component({
  selector: 'app-survey-edit',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, DragDropModule],
  templateUrl: './survey-edit.component.html',
  styleUrl: './survey-edit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SurveyEditComponent implements OnInit, OnDestroy {
  typeId = 0;
  typeName = '';
  /** Bound to the survey name input; synced from typeName when type/version loads. */
  editingTypeName = '';
  nameSaveError: string | null = null;
  version: SurveyVersionDto | null = null;
  versionId = 0;
  surveyStates: SurveyStateDto[] = [];
  questions: SurveyQuestionDto[] = [];
  answersByQuestion: Map<number, SurveyAnswerDto[]> = new Map();
  fragments: PromptFragmentDto[] = [];
  triggersByFragment: Map<number, FragmentTriggerRowDto[]> = new Map();
  thresholds: CallToActionThresholdRowDto[] = [];
  setupPrompt = '';
  collectContactInfo = false;
  scoringGuideTitle = '';
  scoringGuideContent = '';
  /** Improve-prompt: which box has a pending suggestion (key: 'setup' | 'frag-{id}' | 'threshold-{id}'). */
  improveTarget: string | null = null;
  improveSuggested: string | null = null;
  improvePrevious = '';
  improveInProgress = false;
  improveError: string | null = null;
  loading = true;
  saving = false;
  /** When true, show "Saved" on the button briefly after a successful save. */
  saveSuccess = false;
  private saveSuccessTimer: ReturnType<typeof setTimeout> | null = null;
  errorMessage: string | null = null;
  /** Brief "Copied" message after copying survey URL to clipboard. */
  copyUrlFeedback: string | null = null;
  private copyUrlFeedbackTimer: ReturnType<typeof setTimeout> | null = null;
  /** Question IDs that are collapsed (content hidden). Empty = all expanded. */
  collapsedQuestionIds = new Set<number>();
  /** Pessimistic edit lock: we hold the lock and can edit (when draft). */
  editLockHeld = false;
  /** Another admin holds the lock; show read-only and banner. */
  editLockBlockedByOther = false;
  /** Name of admin holding the lock (for banner). */
  lockedByUserName: string | null = null;
  private lockRefreshIntervalId: ReturnType<typeof setInterval> | null = null;
  private static readonly LOCK_REFRESH_MS = 10 * 60 * 1000; // 10 minutes
  private readonly destroy$ = new Subject<void>();

  isQuestionExpanded(questionId: number): boolean {
    return !this.collapsedQuestionIds.has(questionId);
  }

  toggleQuestionCollapsed(questionId: number): void {
    if (this.collapsedQuestionIds.has(questionId)) {
      this.collapsedQuestionIds = new Set(this.collapsedQuestionIds);
      this.collapsedQuestionIds.delete(questionId);
    } else {
      this.collapsedQuestionIds = new Set(this.collapsedQuestionIds).add(questionId);
    }
    this.cdr.markForCheck();
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly admin: AdminService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.admin.getSurveyStates().pipe(takeUntil(this.destroy$)).subscribe((s) => (this.surveyStates = s ?? []));

    this.route.paramMap
      .pipe(
        takeUntil(this.destroy$),
        switchMap((params) => {
          const id = params.get('id');
          this.typeId = id ? parseInt(id, 10) : 0;
          this.loading = true;
          this.errorMessage = null;
          if (!this.typeId) return [];
          return this.admin.getSurveyType(this.typeId);
        })
      )
      .subscribe({
        next: (type) => {
          this.typeName = type?.name ?? '';
          this.editingTypeName = this.typeName;
          this.nameSaveError = null;
          const versionIdParam = this.route.snapshot.queryParamMap.get('versionId');
          const requestedVersionId = versionIdParam ? parseInt(versionIdParam, 10) : 0;
          if (requestedVersionId > 0) {
            this.versionId = requestedVersionId;
            this.admin
              .getSurveyVersionsByType(this.typeId)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  this.loadVersion();
                  this.cdr.markForCheck();
                },
                error: (err: Error) => {
                  this.loading = false;
                  this.errorMessage = err?.message ?? 'Failed to load versions.';
                  this.cdr.markForCheck();
                }
              });
          } else {
            this.loadVersionsAndEdit();
          }
        },
        error: (err: Error) => {
          this.loading = false;
          this.errorMessage = err?.message ?? 'Failed to load survey type.';
        }
      });
  }

  ngOnDestroy(): void {
    if (this.saveSuccessTimer != null) clearTimeout(this.saveSuccessTimer);
    if (this.copyUrlFeedbackTimer != null) clearTimeout(this.copyUrlFeedbackTimer);
    this.releaseLockIfHeld();
    this.stopLockRefresh();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Full URL for respondents to start a new survey for this published version. */
  get publishedSurveyUrl(): string {
    if (!this.versionId || this.isDraft) return '';
    const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
    return `${origin}/intake/create?surveyVersionId=${this.versionId}`;
  }

  copySurveyUrl(): void {
    const url = this.publishedSurveyUrl;
    if (!url) return;
    if (this.copyUrlFeedbackTimer != null) clearTimeout(this.copyUrlFeedbackTimer);
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => {
          this.copyUrlFeedback = 'Copied';
          this.cdr.markForCheck();
          this.copyUrlFeedbackTimer = setTimeout(() => {
            this.copyUrlFeedbackTimer = null;
            this.copyUrlFeedback = null;
            this.cdr.markForCheck();
          }, 2000);
        },
        () => {
          this.copyUrlFeedback = 'Failed to copy';
          this.cdr.markForCheck();
          this.copyUrlFeedbackTimer = setTimeout(() => {
            this.copyUrlFeedbackTimer = null;
            this.copyUrlFeedback = null;
            this.cdr.markForCheck();
          }, 2000);
        }
      );
    } else {
      this.copyUrlFeedback = 'Copy not supported';
      this.cdr.markForCheck();
      this.copyUrlFeedbackTimer = setTimeout(() => {
        this.copyUrlFeedbackTimer = null;
        this.copyUrlFeedback = null;
        this.cdr.markForCheck();
      }, 2000);
    }
  }

  loadVersionsAndEdit(): void {
    this.admin
      .getSurveyVersionsByType(this.typeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (versions) => {
          if (!versions?.length) {
            this.createFirstVersion();
            return;
          }
          const list = versions as Array<{ id: number; surveyStateCode: string }>;
          const draft = list.find((x) => x.surveyStateCode === 'Draft');
          if (draft) {
            this.versionId = draft.id;
            this.loadVersion();
            this.cdr.markForCheck();
            return;
          }
          const published = list.find((x) => x.surveyStateCode === 'Published');
          if (published) {
            this.admin
              .copySurveyVersionToDraft(published.id, {})
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (res: { id?: number }) => {
                  const newId = res?.id;
                  if (newId) {
                    this.versionId = newId;
                    this.loadVersion();
                  }
                  this.cdr.markForCheck();
                },
                error: (err: Error) => {
                  this.loading = false;
                  this.errorMessage = err?.message ?? 'Failed to create draft from published.';
                  this.cdr.markForCheck();
                }
              });
            return;
          }
          this.versionId = list[0].id;
          this.loadVersion();
          this.cdr.markForCheck();
        },
        error: (err: Error) => {
          this.loading = false;
          this.errorMessage = err?.message ?? 'Failed to load versions.';
          this.cdr.markForCheck();
        }
      });
  }

  copyToDraftFromVersion(sourceVersionId: number): void {
    this.admin
      .copySurveyVersionToDraft(sourceVersionId, {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: { id?: number }) => {
          const newId = res?.id;
          if (newId) {
            this.releaseLockIfHeld();
            this.versionId = newId;
            this.loadVersion();
            this.cdr.markForCheck();
          }
        },
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to create draft.';
          this.cdr.markForCheck();
        }
      });
  }

  archiveVersion(versionId: number): void {
    if (!confirm('Archive this version? It will no longer be available for new respondents.')) return;
    this.admin
      .setSurveyVersionState(versionId, { stateCode: 'Archived' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.releaseLockIfHeld();
          this.admin
            .getSurveyVersionsByType(this.typeId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (list) => {
                if (this.versionId === versionId && list?.length) {
                  const arr = list as Array<{ id: number; surveyStateCode: string }>;
                  const draft = arr.find((x) => x.surveyStateCode === 'Draft');
                  const other = arr.find((x) => x.id !== versionId);
                  this.versionId = draft?.id ?? other?.id ?? 0;
                  if (this.versionId) this.loadVersion();
                }
                this.cdr.markForCheck();
              }
            });
        },
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to archive.';
          this.cdr.markForCheck();
        }
      });
  }

  createFirstVersion(): void {
    const draftState = this.surveyStates.find((s) => s.code === 'Draft');
    if (!draftState) {
      this.loading = false;
      this.errorMessage = 'Draft state not found.';
      return;
    }
    this.admin
      .createSurveyVersion({
        surveyTypeId: this.typeId,
        surveyStateId: draftState.id,
        versionNumber: 1,
        setupPrompt: '',
        collectContactInfo: true
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (v) => {
          this.versionId = v.id;
          this.loadVersion();
        },
        error: (err: Error) => {
          this.loading = false;
          this.errorMessage = err?.message ?? 'Failed to create version.';
        }
      });
  }

  loadVersion(): void {
    this.admin
      .getSurveyVersion(this.versionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (v) => {
          this.version = v;
          this.setupPrompt = v?.setupPrompt ?? '';
          this.collectContactInfo = v?.collectContactInfo ?? false;
          this.scoringGuideTitle = v?.scoringGuideTitle ?? '';
          this.scoringGuideContent = v?.scoringGuideContent ?? '';
          this.loadQuestions();
          this.tryAcquireEditLock();
        },
        error: (err: Error) => {
          this.loading = false;
          this.errorMessage = err?.message ?? 'Failed to load version.';
          this.cdr.markForCheck();
        }
      });
  }

  private tryAcquireEditLock(): void {
    const versionId = this.versionId;
    this.admin
      .getEditLockStatus(versionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (status) => {
          // Only show "other admin" when locked and current user is not allowed to edit (server sets canEdit when we hold the lock or auth is disabled with sentinel).
          if (status.isLocked && !status.canEdit) {
            this.editLockBlockedByOther = true;
            this.lockedByUserName = status.lockedByUserName ?? null;
          }
          this.admin
            .acquireEditLock(versionId, { lockExpiresInMinutes: 30 })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res) => {
                if (res.success) {
                  this.editLockHeld = true;
                  this.editLockBlockedByOther = false;
                  this.lockedByUserName = null;
                  this.startLockRefresh();
                } else {
                  this.editLockBlockedByOther = true;
                  this.lockedByUserName = this.lockedByUserName ?? res.message ?? null;
                }
                this.cdr.markForCheck();
              },
              error: () => {
                this.editLockBlockedByOther = true;
                this.cdr.markForCheck();
              }
            });
        },
        error: () => {
          this.editLockBlockedByOther = true;
          this.cdr.markForCheck();
        }
      });
  }

  private releaseLockIfHeld(): void {
    if (!this.editLockHeld || !this.versionId) return;
    const versionId = this.versionId;
    this.editLockHeld = false;
    this.editLockBlockedByOther = false;
    this.lockedByUserName = null;
    this.stopLockRefresh();
    this.admin.releaseEditLock(versionId).pipe(takeUntil(this.destroy$)).subscribe();
    this.cdr.markForCheck();
  }

  private startLockRefresh(): void {
    this.stopLockRefresh();
    const versionId = this.versionId;
    this.lockRefreshIntervalId = setInterval(() => {
      this.admin
        .refreshEditLock(versionId, { lockExpiresInMinutes: 30 })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (r) => {
            if (!r.success) {
              this.editLockHeld = false;
              this.editLockBlockedByOther = true;
              this.stopLockRefresh();
              this.cdr.markForCheck();
            }
          }
        });
    }, SurveyEditComponent.LOCK_REFRESH_MS);
  }

  private stopLockRefresh(): void {
    if (this.lockRefreshIntervalId != null) {
      clearInterval(this.lockRefreshIntervalId);
      this.lockRefreshIntervalId = null;
    }
  }

  loadQuestions(): void {
    this.admin
      .getSurveyQuestionsByVersion(this.versionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (q) => {
          const list = (q ?? []) as SurveyQuestionDto[];
          this.questions = [...list].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
          this.answersByQuestion = new Map();
          this.questions.forEach((qu) => this.loadAnswers(qu.id));
          this.loadFragments();
          this.loadThresholds();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: Error) => {
          this.loading = false;
          this.errorMessage = err?.message ?? 'Failed to load questions.';
          this.cdr.markForCheck();
        }
      });
  }

  loadAnswers(questionId: number): void {
    this.admin.getSurveyAnswersByQuestion(questionId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (a) => {
        const list = (a ?? []) as SurveyAnswerDto[];
        const sorted = [...list].sort((x, y) => (x.displayOrder ?? 0) - (y.displayOrder ?? 0));
        this.answersByQuestion = new Map(this.answersByQuestion).set(questionId, sorted);
        this.cdr.markForCheck();
      }
    });
  }

  loadFragments(): void {
    this.admin.getPromptFragmentsByVersion(this.versionId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (f) => {
        this.fragments = (f ?? []) as PromptFragmentDto[];
        this.triggersByFragment = new Map();
        this.fragments.forEach((fr) => this.loadFragmentTriggers(fr.id));
        this.cdr.markForCheck();
      }
    });
  }

  loadFragmentTriggers(fragmentId: number): void {
    this.admin.getFragmentTriggers(fragmentId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (t) => {
        this.triggersByFragment = new Map(this.triggersByFragment).set(fragmentId, (t ?? []) as FragmentTriggerRowDto[]);
        this.cdr.markForCheck();
      }
    });
  }

  loadThresholds(): void {
    this.admin.getCallToActionThresholdsByVersion(this.versionId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (t) => {
        this.thresholds = [...(t ?? [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
        this.cdr.markForCheck();
      }
    });
  }

  getAnswers(questionId: number): SurveyAnswerDto[] {
    return this.answersByQuestion.get(questionId) ?? [];
  }

  save(): void {
    if (!this.versionId) return;
    this.saving = true;
    this.saveSuccess = false;
    this.errorMessage = null;
    if (this.saveSuccessTimer != null) clearTimeout(this.saveSuccessTimer);
    this.cdr.markForCheck();
    this.admin
      .updateSurveyVersion(this.versionId, {
        setupPrompt: this.setupPrompt,
        collectContactInfo: this.collectContactInfo,
        scoringGuideTitle: this.scoringGuideTitle || null,
        scoringGuideContent: this.scoringGuideContent || null,
        hasBeenPreviewed: false
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.saving = false;
          this.saveSuccess = true;
          this.version = this.version ? { ...this.version, setupPrompt: this.setupPrompt, collectContactInfo: this.collectContactInfo, scoringGuideTitle: this.scoringGuideTitle || null, scoringGuideContent: this.scoringGuideContent || null, hasBeenPreviewed: false } : null;
          this.saveSuccessTimer = setTimeout(() => {
            this.saveSuccessTimer = null;
            this.saveSuccess = false;
            this.cdr.markForCheck();
          }, 2500);
          this.cdr.markForCheck();
        },
        error: (err: Error) => {
          this.saving = false;
          this.errorMessage = err?.message ?? 'Failed to save.';
          this.cdr.markForCheck();
        }
      });
  }

  onQuestionDrop(event: CdkDragDrop<SurveyQuestionDto[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.questions, event.previousIndex, event.currentIndex);
    this.questions.forEach((q, i) => {
      q.displayOrder = i;
      this.updateQuestion(q);
    });
    this.cdr.markForCheck();
  }

  onAnswerDrop(questionId: number, event: CdkDragDrop<SurveyAnswerDto[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    const answers = this.answersByQuestion.get(questionId) ?? [];
    const reordered = [...answers];
    moveItemInArray(reordered, event.previousIndex, event.currentIndex);
    reordered.forEach((a, i) => (a.displayOrder = i));
    this.answersByQuestion = new Map(this.answersByQuestion).set(questionId, reordered);
    reordered.forEach((a) => this.updateAnswer(a));
    this.cdr.markForCheck();
  }

  addQuestion(): void {
    this.admin
      .createSurveyQuestion({
        surveyVersionId: this.versionId,
        questionType: 'Radio',
        questionText: '',
        displayOrder: this.questions.length
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadQuestions(),
        error: (err: Error) => (this.errorMessage = err?.message ?? 'Failed to add question.')
      });
  }

  removeQuestion(question: SurveyQuestionDto): void {
    if (!confirm('Remove this question?')) return;
    this.admin.deleteSurveyQuestion(question.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.loadQuestions();
        this.cdr.markForCheck();
      },
      error: (err: Error) => {
        this.errorMessage = err?.message ?? 'Failed to remove question.';
        this.cdr.markForCheck();
      }
    });
  }

  updateQuestion(question: SurveyQuestionDto): void {
    this.admin
      .updateSurveyQuestion(question.id, {
        questionType: question.questionType,
        questionText: question.questionText,
        displayOrder: question.displayOrder,
        maxCharacterCount: question.maxCharacterCount
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to update question.';
          this.cdr.markForCheck();
        }
      });
  }

  addAnswer(question: SurveyQuestionDto): void {
    this.admin
      .createSurveyAnswer({
        surveyQuestionId: question.id,
        answerText: '',
        score: 0,
        displayOrder: this.getAnswers(question.id).length
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadAnswers(question.id),
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to add answer.';
          this.cdr.markForCheck();
        }
      });
  }

  updateAnswer(answer: SurveyAnswerDto): void {
    this.admin
      .updateSurveyAnswer(answer.id, {
        answerText: answer.answerText,
        score: answer.score,
        displayOrder: answer.displayOrder
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to update answer.';
          this.cdr.markForCheck();
        }
      });
  }

  removeAnswer(answer: SurveyAnswerDto): void {
    if (!confirm('Remove this answer?')) return;
    this.admin.deleteSurveyAnswer(answer.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadAnswers(answer.surveyQuestionId),
      error: (err: Error) => {
        this.errorMessage = err?.message ?? 'Failed to remove answer.';
        this.cdr.markForCheck();
      }
    });
  }

  getFragmentTriggers(fragmentId: number): FragmentTriggerRowDto[] {
    return this.triggersByFragment.get(fragmentId) ?? [];
  }

  getAnswerText(questionId: number, answerId: number): string {
    const a = this.getAnswers(questionId).find((x) => x.id === answerId);
    return a?.answerText ?? 'Answer ' + answerId;
  }

  addFragment(question: SurveyQuestionDto): void {
    this.admin
      .createPromptFragment({
        surveyVersionId: this.versionId,
        surveyQuestionId: question.id,
        fragmentText: ''
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadFragments(),
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to add fragment.';
          this.cdr.markForCheck();
        }
      });
  }

  updateFragment(fragment: PromptFragmentDto): void {
    this.admin
      .updatePromptFragment(fragment.id, { fragmentText: fragment.fragmentText })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to update fragment.';
          this.cdr.markForCheck();
        }
      });
  }

  removeFragment(fragment: PromptFragmentDto): void {
    if (!confirm('Remove this prompt fragment?')) return;
    this.admin.deletePromptFragment(fragment.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadFragments(),
      error: (err: Error) => {
        this.errorMessage = err?.message ?? 'Failed to remove fragment.';
        this.cdr.markForCheck();
      }
    });
  }

  onAddFragmentTrigger(fragment: PromptFragmentDto, selectEl: HTMLSelectElement): void {
    const id = parseInt(selectEl.value, 10);
    if (!Number.isInteger(id) || id < 1) return;
    this.addFragmentTrigger(fragment, id);
  }

  addFragmentTrigger(fragment: PromptFragmentDto, surveyAnswerId: number): void {
    this.admin.addFragmentTrigger(fragment.id, { surveyAnswerId }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadFragmentTriggers(fragment.id),
      error: (err: Error) => {
        this.errorMessage = err?.message ?? 'Failed to add trigger.';
        this.cdr.markForCheck();
      }
    });
  }

  removeFragmentTrigger(fragmentId: number, surveyAnswerId: number): void {
    this.admin.removeFragmentTrigger(fragmentId, surveyAnswerId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadFragmentTriggers(fragmentId),
      error: (err: Error) => {
        this.errorMessage = err?.message ?? 'Failed to remove trigger.';
        this.cdr.markForCheck();
      }
    });
  }

  addThreshold(): void {
    this.admin
      .createCallToActionThreshold({
        surveyVersionId: this.versionId,
        thresholdOperator: '>=',
        thresholdValue: 40,
        displayText: '',
        promptFragmentText: '',
        displayOrder: this.thresholds.length
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadThresholds(),
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to add threshold.';
          this.cdr.markForCheck();
        }
      });
  }

  updateThreshold(t: CallToActionThresholdRowDto): void {
    this.admin
      .updateCallToActionThreshold(t.id, {
        thresholdOperator: t.thresholdOperator,
        thresholdValue: t.thresholdValue,
        displayText: t.displayText,
        promptFragmentText: t.promptFragmentText,
        displayOrder: t.displayOrder
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to update threshold.';
          this.cdr.markForCheck();
        }
      });
  }

  removeThreshold(t: CallToActionThresholdRowDto): void {
    if (!confirm('Remove this threshold?')) return;
    this.admin.deleteCallToActionThreshold(t.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadThresholds(),
      error: (err: Error) => {
        this.errorMessage = err?.message ?? 'Failed to remove threshold.';
        this.cdr.markForCheck();
      }
    });
  }

  goPreview(): void {
    this.router.navigate(['/admin/survey-types', this.typeId, 'preview'], { queryParams: { versionId: this.versionId } });
  }

  get versionStateCode(): string {
    return this.version?.surveyStateCode ?? 'Draft';
  }

  get isDraft(): boolean {
    return this.versionStateCode === 'Draft';
  }

  get hasBeenPreviewed(): boolean {
    return this.version?.hasBeenPreviewed ?? false;
  }

  /** Editable only when version is Draft and we hold the edit lock. */
  get canEditVersion(): boolean {
    return this.isDraft && this.editLockHeld;
  }

  publishVersion(): void {
    if (!this.versionId || !this.isDraft) return;
    if (!confirm('Publish this survey version? It will be available for respondents.')) return;
    this.admin
      .setSurveyVersionState(this.versionId, { stateCode: 'Published' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadVersion(),
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to publish.';
          this.cdr.markForCheck();
        }
      });
  }

  saveSurveyName(): void {
    if (!this.typeId) return;
    const name = (this.editingTypeName ?? '').trim();
    if (!name) {
      this.nameSaveError = 'Survey name is required.';
      this.cdr.markForCheck();
      return;
    }
    if (name === this.typeName) {
      this.nameSaveError = null;
      this.cdr.markForCheck();
      return;
    }
    this.nameSaveError = null;
    this.admin
      .updateSurveyType(this.typeId, { name })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.typeName = name;
          this.cdr.markForCheck();
        },
        error: (err: Error) => {
          this.nameSaveError = err?.message ?? 'Failed to save name.';
          this.cdr.markForCheck();
        }
      });
  }

  getImprovePromptText(key: string): string {
    if (key === 'setup') return this.setupPrompt?.trim() ?? '';
    if (key.startsWith('frag-')) {
      const id = parseInt(key.slice(5), 10);
      const f = this.fragments.find((x) => x.id === id);
      return f?.fragmentText?.trim() ?? '';
    }
    if (key.startsWith('threshold-')) {
      const id = parseInt(key.slice(10), 10);
      const t = this.thresholds.find((x) => x.id === id);
      return t?.promptFragmentText?.trim() ?? '';
    }
    return '';
  }

  improvePrompt(key: string): void {
    const text = this.getImprovePromptText(key);
    if (!text) return;
    this.improveError = null;
    this.improveTarget = null;
    this.improveInProgress = true;
    this.improvePrevious = text;
    this.cdr.markForCheck();
    this.admin.promptRewrite({ prompt: text }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.improveError = null;
        this.improveTarget = key;
        this.improveSuggested = res.recommendedPrompt ?? null;
        this.improveInProgress = false;
        this.cdr.markForCheck();
      },
      error: (err: Error) => {
        this.improveError = err?.message ?? 'Failed to get recommendation.';
        this.improveInProgress = false;
        this.improvePrevious = '';
        this.cdr.markForCheck();
      }
    });
  }

  useImprove(): void {
    if (this.improveTarget == null || this.improveSuggested == null) return;
    const key = this.improveTarget;
    if (key === 'setup') {
      this.setupPrompt = this.improveSuggested;
      this.save();
    } else if (key.startsWith('frag-')) {
      const id = parseInt(key.slice(5), 10);
      const f = this.fragments.find((x) => x.id === id);
      if (f) {
        f.fragmentText = this.improveSuggested;
        this.updateFragment(f);
      }
    } else if (key.startsWith('threshold-')) {
      const id = parseInt(key.slice(10), 10);
      const t = this.thresholds.find((x) => x.id === id);
      if (t) {
        t.promptFragmentText = this.improveSuggested;
        this.updateThreshold(t);
      }
    }
    this.improveTarget = null;
    this.improveSuggested = null;
    this.improvePrevious = '';
    this.cdr.markForCheck();
  }

  revertImprove(): void {
    if (this.improveTarget == null) return;
    this.improveError = null;
    const key = this.improveTarget;
    if (key === 'setup') this.setupPrompt = this.improvePrevious;
    else if (key.startsWith('frag-')) {
      const id = parseInt(key.slice(5), 10);
      const f = this.fragments.find((x) => x.id === id);
      if (f) f.fragmentText = this.improvePrevious;
    } else if (key.startsWith('threshold-')) {
      const id = parseInt(key.slice(10), 10);
      const t = this.thresholds.find((x) => x.id === id);
      if (t) t.promptFragmentText = this.improvePrevious;
    }
    this.improveTarget = null;
    this.improveSuggested = null;
    this.improvePrevious = '';
    this.cdr.markForCheck();
  }
}
